import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Rideable } from '../interfaces/Rideable';

export class Frog implements GameObject {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public size: Vector2;
  
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private gridSize: number;
  
  // Animation state
  private hopProgress: number = 0;
  private isHopping: boolean = false;
  private hopStartPos: Vector2;
  private hopTargetPos: Vector2;
  private readonly HOP_DURATION: number = 0.15; // 150ms hop animation
  
  // State
  private isDead: boolean = false;
  private deathTimer: number = 0;
  private readonly DEATH_DURATION: number = 1.0;
  
  // Riding state (for logs, turtles, and safe crocodiles)
  private ridingObject: Rideable | null = null;
  
  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gridSize: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.size = { x: gridSize, y: gridSize };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gridSize = gridSize;
    
    this.hopStartPos = { ...this.position };
    this.hopTargetPos = { ...this.position };
  }

  public update(deltaTime: number): void {
    if (this.isDead) {
      this.deathTimer += deltaTime;
      return;
    }
    
    // Update hop animation
    if (this.isHopping) {
      this.hopProgress += deltaTime / this.HOP_DURATION;
      
      if (this.hopProgress >= 1.0) {
        // Hop complete
        this.hopProgress = 1.0;
        this.isHopping = false;
        this.position = { ...this.hopTargetPos };
      } else {
        // Interpolate position during hop with arc
        const t = this.hopProgress;
        // Ease out for more natural movement
        const easeT = 1 - Math.pow(1 - t, 3);
        
        this.position.x = this.hopStartPos.x + (this.hopTargetPos.x - this.hopStartPos.x) * easeT;
        this.position.y = this.hopStartPos.y + (this.hopTargetPos.y - this.hopStartPos.y) * easeT;
      }
    }
  }

  public hop(deltaX: number, deltaY: number): boolean {
    if (this.isHopping || this.isDead) {
      return false; // Can't hop while already hopping or dead
    }
    
    const newX = this.position.x + deltaX * this.gridSize;
    const newY = this.position.y + deltaY * this.gridSize;
    
    // Validate bounds (15 columns, 14 rows)
    const gridX = Math.floor(newX / this.gridSize);
    const gridY = Math.floor(newY / this.gridSize);
    
    if (gridX < 0 || gridX >= 15 || gridY < 0 || gridY >= 14) {
      return false; // Out of bounds
    }
    
    // Start hop animation
    this.hopStartPos = { ...this.position };
    this.hopTargetPos = { x: newX, y: newY };
    this.hopProgress = 0;
    this.isHopping = true;
    this.ridingObject = null; // Stop riding when hopping
    
    // Play hop sound
    this.audioManager.playMenuMove();
    
    return true;
  }

  public setPosition(x: number, y: number): void {
    this.position = { x, y };
    this.hopStartPos = { ...this.position };
    this.hopTargetPos = { ...this.position };
    this.hopProgress = 0;
    this.isHopping = false;
    this.ridingObject = null;
  }
  
  public revive(): void {
    this.isDead = false;
    this.deathTimer = 0;
    this.isHopping = false;
    this.ridingObject = null;
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public getBounds(): Rectangle {
    // Frog hitbox is slightly smaller than grid cell for more forgiving gameplay
    const hitboxSize = this.gridSize * 0.8;
    const offset = (this.gridSize - hitboxSize) / 2;
    
    return {
      x: this.position.x + offset,
      y: this.position.y + offset,
      width: hitboxSize,
      height: hitboxSize
    };
  }

  public die(): void {
    this.isDead = true;
    this.deathTimer = 0;
    this.isHopping = false;
    this.ridingObject = null;
    this.audioManager.playExplosion();
  }

  public isDying(): boolean {
    return this.isDead && this.deathTimer < this.DEATH_DURATION;
  }

  public isRiding(): boolean {
    return this.ridingObject !== null;
  }

  public getRidingObject(): Rideable | null {
    return this.ridingObject;
  }

  public setRiding(object: Rideable | null): void {
    this.ridingObject = object;
  }

  public moveWithRiding(deltaX: number, deltaY: number): void {
    if (this.ridingObject && !this.isHopping) {
      this.position.x += deltaX;
      this.position.y += deltaY;
      
      // Check if frog has been carried off screen
      if (this.position.x < -this.gridSize || this.position.x > 15 * this.gridSize) {
        this.die();
      }
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Note: Offsets will be handled by the game when calling this method
    if (this.isDead) {
      this.renderDeathAnimation(this.position.x, this.position.y);
    } else {
      this.renderFrog(this.position.x, this.position.y);
    }
  }

  public renderWithOffset(ctx: CanvasRenderingContext2D, offsetX: number, offsetY: number): void {
    const renderX = offsetX + this.position.x;
    const renderY = offsetY + this.position.y;
    
    if (this.isDead) {
      this.renderDeathAnimation(renderX, renderY);
    } else {
      this.renderFrog(renderX, renderY);
    }
  }

  private renderFrog(x: number, y: number): void {
    const size = this.gridSize;
    const centerX = x + size / 2;
    const centerY = y + size / 2;
    
    if (this.isHopping) {
      // Add a slight bounce effect during hopping
      const bounceHeight = Math.sin(this.hopProgress * Math.PI) * 8;
      const adjustedY = y - bounceHeight;
      
      this.drawFrogBody(centerX, adjustedY + size / 2, size, '#00FF00');
    } else {
      this.drawFrogBody(centerX, centerY, size, '#00FF00');
    }
    
    // Add slight transparency if riding something
    if (this.ridingObject) {
      this.renderManager.fillRect(x, y, size, size, 'rgba(255, 255, 255, 0.2)');
    }
  }

  private drawFrogBody(centerX: number, centerY: number, size: number, color: string): void {
    const bodySize = size * 0.6;
    const legSize = size * 0.25;
    
    // Draw body (oval)
    this.renderManager.drawEllipse(
      centerX - bodySize / 2,
      centerY - bodySize / 2,
      bodySize / 2,
      bodySize * 0.4,
      color,
      true
    );
    
    // Draw eyes (two small circles at top)
    const eyeSize = size * 0.15;
    const eyeOffset = bodySize * 0.25;
    
    this.renderManager.drawEllipse(
      centerX - eyeOffset - eyeSize / 2,
      centerY - bodySize * 0.3 - eyeSize / 2,
      eyeSize / 2,
      eyeSize / 2,
      '#FFFF00',
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + eyeOffset - eyeSize / 2,
      centerY - bodySize * 0.3 - eyeSize / 2,
      eyeSize / 2,
      eyeSize / 2,
      '#FFFF00',
      true
    );
    
    // Draw legs (four small ovals)
    const legOffsetX = bodySize * 0.4;
    const legOffsetY = bodySize * 0.2;
    
    // Front legs
    this.renderManager.drawEllipse(
      centerX - legOffsetX - legSize / 2,
      centerY - legOffsetY - legSize / 2,
      legSize / 2,
      legSize * 0.75,
      color,
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + legOffsetX - legSize / 2,
      centerY - legOffsetY - legSize / 2,
      legSize / 2,
      legSize * 0.75,
      color,
      true
    );
    
    // Back legs (larger)
    const backLegSize = legSize * 1.2;
    this.renderManager.drawEllipse(
      centerX - legOffsetX - backLegSize / 2,
      centerY + legOffsetY - backLegSize / 2,
      backLegSize / 2,
      backLegSize * 0.65,
      color,
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + legOffsetX - backLegSize / 2,
      centerY + legOffsetY - backLegSize / 2,
      backLegSize / 2,
      backLegSize * 0.65,
      color,
      true
    );
  }

  private renderDeathAnimation(x: number, y: number): void {
    const size = this.gridSize;
    const centerX = x + size / 2;
    const centerY = y + size / 2;
    
    // Flash effect
    const flashAlpha = Math.sin(this.deathTimer * 20) * 0.5 + 0.5;
    const color = `rgba(255, 0, 0, ${flashAlpha})`;
    
    // Draw death effect - expanding circle
    const expansionRadius = (this.deathTimer / this.DEATH_DURATION) * size;
    
    for (let i = 0; i < 5; i++) {
      const radius = expansionRadius - i * 5;
      if (radius > 0) {
        const alpha = 1 - (this.deathTimer / this.DEATH_DURATION);
        this.renderManager.drawEllipse(
          centerX - radius,
          centerY - radius,
          radius,
          radius,
          `rgba(255, ${i * 50}, 0, ${alpha})`,
          false,
          2
        );
      }
    }
  }
}